import { useSortableTable } from '../../../hooks/useSortableTable';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigateWithPreference } from '../../../utils/navigationHelper';
import { apiFetch } from '../../../utils/api';
import PageHeader from '../../../components/PageHeader';

function Suppliers() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const { items: sortedSuppliers, requestSort, getSortIndicator } = useSortableTable(suppliers, { key: 'company_name', direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoadError('');
      const res = await apiFetch('/api/suppliers');
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load suppliers');
      }
      setSuppliers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setLoadError(err.message || 'Failed to load suppliers');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSupplier = async (supplierId, companyName) => {
    if (!window.confirm(`Delete ${companyName}? This will also delete all associated data.`)) return;

    try {
      const response = await apiFetch(`/api/suppliers/${supplierId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchSuppliers();
        alert('Supplier deleted successfully');
      } else {
        alert('Failed to delete supplier');
      }
    } catch (error) {
      console.error('Error deleting supplier:', error);
      alert('Error deleting supplier');
    }
  };

  const filteredSuppliers = sortedSuppliers.filter(supplier =>
    (supplier.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supplier.supplier_code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supplier.contact_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supplier.city || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Suppliers"
        subtitle={`${suppliers.length} total`}
        backButton
        onBack={() => navigate('/crm')}
        backTitle="Back to CRM"
        sticky
        actionButton={
          <button
            onClick={() => navigate('/settings')}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            📥 Import Suppliers
          </button>
        }
      />

      <div className="px-4 py-3">
        {loadError && (
          <div className="mb-3 bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
            Supplier API error: {loadError}
          </div>
        )}

        <div className="mb-3">
          <input
            type="text"
            placeholder="🔍 Search suppliers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-auto max-h-[calc(100vh-240px)] relative">
            <table className="w-full">
              <thead className="bg-gray-300 sticky top-0 z-20 border-b border-gray-400">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('company_name')}>Company{getSortIndicator('company_name')}</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('supplier_code')}>Code{getSortIndicator('supplier_code')}</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('contact_name')}>Contact{getSortIndicator('contact_name')}</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('phone')}>Phone{getSortIndicator('phone')}</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('email')}>Email{getSortIndicator('email')}</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('city')}>City{getSortIndicator('city')}</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSuppliers.map((supplier, index) => (
                  <tr
                    key={supplier.id}
                    onClick={() => navigateWithPreference(navigate, `/crm/suppliers/${supplier.id}`, 'suppliers')}
                    className={`hover:bg-blue-200 cursor-pointer transition border-b border-gray-100 ${index % 2 === 1 ? 'bg-blue-100' : 'bg-white'}`}
                  >
                    <td className="px-3 py-2"><div className="font-medium text-gray-900 text-sm">{supplier.company_name}</div></td>
                    <td className="px-3 py-2"><span className="text-xs font-mono text-gray-600">{supplier.supplier_code || '—'}</span></td>
                    <td className="px-3 py-2 text-sm text-gray-700">{supplier.contact_name || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{supplier.phone || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{supplier.email || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{supplier.city || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/crm/suppliers/${supplier.id}`);
                        }}
                        className="text-purple-600 hover:text-purple-800 text-xs font-medium mr-3"
                      >
                        View
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSupplier(supplier.id, supplier.company_name);
                        }}
                        className="text-red-600 hover:text-red-800 text-xs font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredSuppliers.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏭</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No suppliers found</h3>
              <p className="text-gray-500 mb-4">{searchTerm ? 'Try a different search term' : 'Add your first supplier to get started'}</p>
            </div>
          )}
        </div>

        <div className="mt-2 text-xs text-gray-600">
          Showing {filteredSuppliers.length} of {suppliers.length}
        </div>
      </div>
    </div>
  );
}

export default Suppliers;
